// SF2 — Price Elasticity Estimator.
//
// THE RANGE IS THE OUTPUT. A seller shown "-1.8" acts on it; a seller shown
// "-0.4 to -3.1" knows the evidence will not carry a price move. So the range
// is rendered as the primary figure and the midpoint is deliberately not given
// its own prominence — reducing this to one number would undo the sub-feature.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Pricing.module.css';
import { getElasticity } from '../services/pricingApi';

const Elasticity = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try { setData(await getElasticity(token)); }
    catch (err) { setError(err); setData(null); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>How each product responds to price</h2>
        {data && (
          <span className={local.muted}>
            {data.measured} measured · {data.unmeasured} not measurable
          </span>
        )}
      </div>
      <p className={local.muted}>
        Shown as a range, not a number. A wide range means the evidence will not
        support a price move, and that is the useful finding.
      </p>

      {error && (
        <div className={`${local.banner} ${local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div><strong>Could not load price response</strong><div>{error.message}</div></div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th>Product</th>
                <th className={local.numeric}>Response range</th>
                <th className={local.numeric}>Episodes</th>
                <th>Basis</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td className={local.emptyCell} colSpan={4}>
                  {loading ? 'Loading…' : 'No products with enough price variation yet.'}
                </td></tr>
              ) : rows.map((row) => (
                <tr key={row.sku}>
                  <td>{row.sku}</td>
                  <td className={local.numeric}>
                    {row.available
                      ? <strong>{row.range.low} to {row.range.high}</strong>
                      : <span className={local.blank} title={row.reason}>not measurable</span>}
                  </td>
                  <td className={`${local.numeric} ${local.context}`}>{row.points ?? '—'}</td>
                  <td className={local.context}>
                    {row.available ? row.basis
                      : row.inherited
                        ? <span title={row.inherited.reason}>inherited (not grounds for a move)</span>
                        : (row.reason || '').slice(0, 60)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.slice(0, 6).map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default Elasticity;
